import { AlertTriangle, Plus, Trash2 } from "lucide-react";

/**
 * Inline editor for caregiver certifications. Each row = {name, number,
 * expires_on}. Surfaces "expires in N days" / "expired" warnings inline.
 * Used in the create/edit user modal in AdminTeam.
 */
export default function CertificationsEditor({ items, onChange, testidPrefix = "cert" }) {
  const add = () =>
    onChange([
      ...(items || []),
      {
        _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: "",
        number: "",
        expires_on: "",
      },
    ]);
  const update = (idx, patch) =>
    onChange(items.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {(items || []).map((c, idx) => {
        const exp = c.expires_on ? new Date(c.expires_on + "T00:00:00") : null;
        const daysLeft = exp
          ? Math.ceil((exp.getTime() - Date.now()) / 86400000)
          : null;
        const expSoon = daysLeft != null && daysLeft <= 30;
        const expired = daysLeft != null && daysLeft < 0;
        const stableKey =
          c._key || `${c.name || ""}|${c.number || ""}|${c.expires_on || ""}|${idx}`;
        return (
          <div
            key={stableKey}
            className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start"
            data-testid={`${testidPrefix}-row-${idx}`}
          >
            <input
              value={c.name}
              onChange={(e) => update(idx, { name: e.target.value })}
              placeholder="e.g. CNA"
              className="input-clinical sm:col-span-3"
              data-testid={`${testidPrefix}-name-${idx}`}
            />
            <input
              value={c.number}
              onChange={(e) => update(idx, { number: e.target.value })}
              placeholder="License #"
              className="input-clinical sm:col-span-4"
              data-testid={`${testidPrefix}-number-${idx}`}
            />
            <input
              type="date"
              value={c.expires_on || ""}
              onChange={(e) => update(idx, { expires_on: e.target.value })}
              className="input-clinical sm:col-span-4"
              data-testid={`${testidPrefix}-expires-${idx}`}
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="btn-ghost !p-2 sm:col-span-1 hover:!text-destructive"
              data-testid={`${testidPrefix}-remove-${idx}`}
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            {exp && (expSoon || expired) && (
              <div
                className={`sm:col-span-12 -mt-1 text-[11px] flex items-center gap-1 ${
                  expired ? "text-destructive" : "text-amber-700"
                }`}
              >
                <AlertTriangle className="h-3 w-3" />
                {expired
                  ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? "" : "s"} ago`
                  : `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="btn-ghost text-clinical-primary"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="h-4 w-4" /> Add certification
      </button>
    </div>
  );
}
